(function(angular) {
    /**
     * @typedef {('asc'|'desc')} gridTableSort
     */

    /**
     * @typedef {(string|Function)} gridTableColumnFieldSpec
     */

    /**
     * @typedef {Object} gridTableColumnConfigurable
     * @property {boolean} [visible] Defines if the visibility of the column is configurable
     * @property {(boolean|number|'first'|'last')} [position] Defines if the position of the column is configurable. Providing a number or constants like 'last' will stick the column to that position.
     */

    /**
     * @typedef {Object} GridTableColumn
     * @property {string} key The column key is a unique identifier of the column
     * @property {string} title The column title
     * @property {gridTableColumnFieldSpec} fieldSpec The property, the lodash notation object path or a closure to retrieve the value from the row. Example `updatedAt`, `audit.updatedAt`, `function(row) { return row.updatedAt; }`
     * @property {gridTableColumnFieldSpec} [rawFieldSpec] While fieldSpec retrieves a "presentational" value, this one allows to retrieve a RAW value for a column, to be used for sorting in case the model and view value differs. Eg. fieldSpec might return a formatted date to be displayed in the grid, while the rawFieldSpec might return the timestamp to be used to sort the column
     * @property {boolean} [visible] Defines if the column is currently visible in the table. Optional.
     * @property {number} [size] A predefined size from 1 to 12 that expands the column width. Alternatively className could be used with `flex-grow`, `flex-basis` and `max-width` styles to control the width of the column. Optional.
     * @property {string} [className] Column CSS class name, can be used to change the column styling from outside the component. Optional.
     * @property {gridTableSort} [order] The sorting to apply to the column on initialization. Optional.
     * @property {boolean} [sortable] Is the user allowed to sort by this column. Optional.
     * @property {boolean|gridTableColumnConfigurable} [configurable] Is the column configuration allowed to be altered by the user in the grid configuration screen. While boolean version controls all-or-none, the Object version could be provided for a more fine-grained control, eg `{visible: false}` would allow the column to be displayed in the configuration screen but the visible property will be read-only. Optional.
     * @property {boolean} [filterable] Is the column eligible to be used as part of filtering process. Optional.
     * @property {boolean} [showTooltip] Should the column cells display the value of the cell as tooltip. Optional.
     * @property {boolean} [editable] True if the current cell is editable. Optional.
     */

    /**
     * @typedef {Object} SerializableGridTableColumn
     * @property {string} key The column key is a unique identifier of the column
     * @property {boolean} visible Defines if the column is currently visible in the table
     * @property {gridTableSort} [order] The sorting to apply to the column on initialization. Optional
     */

    /**
     * @typedef {Object} GridTableFilter
     * @property {string} value The filter value to apply. When no whitelist or blacklist columns provided, all columns are qualifying for filtering.
     * @property {Array<string>} [whitelistColumnKeys] A list of column keys to use when applying the filter. Interchangeable with blacklistColumnKeys.
     * @property {Array<string>} [blacklistColumnKeys] A list of column keys to exclude when applying the filter. Interchangeable with whitelistColumnKeys.
     */

    /**
     * @typedef {GridTableColumn} GridTableGroupColumn
     */

    /**
     * Unique grid identifier generator
     */
    const nextUid = (function() {
        let uid = 0;

        return function() {
            return `grid-table-${++uid}`;
        };
    })();

    /**
     * @ngdoc component
     * @name GridTableController
     * @description List table component controller
     */
    class GridTableController {
        static get $inject() {
            return ['$injector', '$element'];
        }

        constructor($injector, $element) {
            const GridTableColumnsFactory = $injector.get('GridTableColumnsFactory');
            const GridTableDatasetFactory = $injector.get('GridTableDatasetFactory');
            const GridTableScrollerFactory = $injector.get('GridTableScrollerFactory');
            const GridTableRearrangeFactory = $injector.get('GridTableRearrangeFactory');

            this.$document = $injector.get('$document');
            this.SCROLLBAR_WIDTH = $injector.get('SCROLLBAR_WIDTH');

            /**
             * Unique Id used to distinguish between multiple instances of the component
             * @type {string}
             */
            this.gridId = nextUid();

            /**
             * @type {GridTableColumnsFactory}
             */
            this.groupColumns$ = new GridTableColumnsFactory([], true);

            /**
             * @type {GridTableColumnsFactory}
             */
            this.columns$ = new GridTableColumnsFactory([], false);

            /**
             * @type {GridTableDatasetFactory}
             */
            this.dataset$ = new GridTableDatasetFactory(this.columns$, this.groupColumns$);

            /**
             * @type {GridTableScrollerFactory}
             */
            this.scroller$ = new GridTableScrollerFactory($element, () => this.dataset$.renderNextPage());

            /**
             * @type {GridTableRearrangeFactory}
             */
            this.rearrange$ = new GridTableRearrangeFactory($element, this.dataset$);

            /**
             * Indicates that the grid row is click-able
             * @type {boolean}
             */
            this.hasActionableRow = false;

            /**
             * If is set identifies the row to be edited.
             * (will help to enable the edit view for editable cells belongs to that row)
             * @type {string}
             */
            this.editRowKey = '';

            /**
             * Indicates that the grid must enable the drag and drop functionality. It is enabled if the parent component provides a callback for `onRearrange`.
             * @type {boolean}
             */
            this.hasRearrangeCapabilities = false;

            /**
             * Contains the columns visibility stylesheet element relevant to the current component instance
             * Used to control visibility of columns.
             * Opted for this approach to avoid any watcher on grid cells due to angular performance on large datasets
             * @type {Object}
             */
            this.$visibilityStyle = this.createStyleElement('column-visibility');

            this.$sortingStyle = this.createStyleElement('sorting');
        }

        /**
         * @ngdoc property
         * @name GridTableController#loadingProgress
         * @description Current loading progress 0% - 100%
         * @returns {number}
         */
        get loadingProgress() {
            return this.dataset$.loadingProgress;
        }

        /**
         * @ngdoc property
         * @name GridTableController#gridClassName
         * @description The grid class name
         * @returns {string}
         */
        get gridClassName() {
            return `${this.gridId} ${this.fillContainer ? 'grid-fill-container' : ''}`;
        }

        /**
         * @ngdoc property
         * @name GridTableController#gridCanRearrange
         * @description Grid allows rearrange
         * @returns {boolean}
         */
        get gridCanRearrange() {
            return this.group && this.hasRearrangeCapabilities && this.enableRearrange && !this.dataset$.isHydrating;
        }

        $onInit() {
            this.hasActionableRow = _.isFunction(this.onRowClick);
            this.hasRearrangeCapabilities = _.isFunction(this.onRearrange);

            this
                .ensureValue('rowKey', 'id')
                .ensureValue('rowName', 'title')
                .ensureValue('filters', [])
                .ensureValue('sortable', true)
                .ensureValue('onColumnsChange', angular.noop)
                .ensureValue('onRowClick', angular.noop)
                .ensureValue('onOpenSettingsModal', angular.noop)
                .ensureValue('onCancelSettingsModal', angular.noop)
                .ensureValue('onGridRendered', angular.noop)
                .ensureValue('defaultExpandRows', true)
                .ensureValue('expandedRowKeys', [])
                .ensureValue('fillContainer', false)
                .ensureValue('group', false)
                .ensureValue('groupBy', '')
                .ensureValue('groupColumns', [])
                .ensureValue('readOnlyRowKeys', [])
                .ensureValue('onSelectedRowsChange', angular.noop)
                .ensureValue('onEditCellComplete', angular.noop)
                .ensureValue('onRearrange', angular.noop)
            ;
        }

        $postLink() {
            this.scroller$.setScrollModeContainer(this.fillContainer);
            this.scroller$.onInit();

            if (this.hasRearrangeCapabilities) {
                this.rearrange$.setOnRearrangeCallback(this.onRearrange);
                this.rearrange$.onInit();
                this.propagateRearrangeState();
            }
        }

        $onChanges(changelist) {
            this.columns$.setOnChangeCallback(this.onColumnsChange);

            if (changelist.columns) {
                this.columns$.hydrate(this.columns);
                this.updateColumnsVisibility();

                const sortingColumn = this.columns$.getSortingColumn();

                if (sortingColumn) {
                    this.dataset$.applySorting(sortingColumn.key, sortingColumn.order);
                    this.updateColumnsSorting();
                }
            }

            if (changelist.dataset) {
                this.dataset$.hydrate(this.dataset)
                    .then(() => {
                        return this.notifyRowSelectionChange();
                    });
            }

            if (changelist.rowKey) {
                this.dataset$.setRowKey(this.rowKey);
            }

            if (changelist.sortable) {
                this.columns$.setIsSortable(this.sortable);
            }

            if (changelist.filters) {
                this.dataset$.resetFilters();

                _.forEach(changelist.filters.currentValue, (filter) => {
                    const eligibleColumns = this.columns$.getEligibleFilterFieldSpecs(filter);
                    const eligibleGroupColumns = this.groupColumns$.getEligibleFilterFieldSpecs(filter);

                    this.dataset$.storeFilterArguments(filter, eligibleColumns, eligibleGroupColumns);
                    this.dataset$.applyFilter(filter, eligibleColumns);
                    this.dataset$.applyGroupFilter(filter, eligibleGroupColumns);
                });
            }

            if (changelist.group) {
                this.dataset$.setGroupedView(this.group);

                this.propagateRearrangeState();
            }

            if (changelist.groupBy) {
                this.dataset$.setGroupBy(this.groupBy);
            }

            if (changelist.groupColumns) {
                this.groupColumns$.hydrate(this.groupColumns);
            }

            if (changelist.readOnlyRowKeys) {
                this.dataset$.setReadOnlyRowKeys(this.readOnlyRowKeys);
            }

            if (changelist.extraGroups && _.isArray(this.extraGroups)) {
                this.dataset$.setExtraGroups(this.extraGroups);
            }

            if (changelist.enableRearrange) {
                this.propagateRearrangeState();
            }
        }

        $onDestroy() {
            this.$document.get(0).head.removeChild(this.$visibilityStyle);
            this.$document.get(0).head.removeChild(this.$sortingStyle);

            this.scroller$.onDestroy();
            this.rearrange$.onDestroy();
            this.columns$.onDestroy();
            this.dataset$.onDestroy();
        }

        createStyleElement(context = '') {
            const $styleElement = this.$document.get(0).createElement('style');

            $styleElement.setAttribute('type', 'text/css');
            $styleElement.setAttribute('media', 'screen');
            _.set($styleElement, 'dataset.id', this.gridId);
            _.set($styleElement, 'dataset.context', context);
            $styleElement.appendChild(this.$document.get(0).createTextNode(''));

            this.$document.get(0).head.appendChild($styleElement);

            return $styleElement;
        }

        removeCssRules($styleElement) {
            const sheet = $styleElement.sheet;
            const cssRules = sheet.rules || sheet.cssRules || [];
            const size = cssRules.length;

            for (let i = 0; i < size; i++) {
                sheet.deleteRule(0);
            }
        }

        addCSSRule($styleElement, selector, rules) {
            const sheet = $styleElement.sheet;

            if ('insertRule' in sheet) {
                sheet.insertRule(`${selector} {${rules}}`, 0);
            } else if ('addRule' in sheet) {
                sheet.addRule(selector, rules, 0);
            }

            return this;
        }

        updateColumnsVisibility() {
            const hiddenColumns = this.columns$.hiddenColumns;
            const firstVisibleColumn = _.first(this.columns$.visibleColumns);

            this.removeCssRules(this.$visibilityStyle);

            _.forEach(hiddenColumns, (column) => {
                this.addCSSRule(
                    this.$visibilityStyle,
                    `.${this.gridId} ${this.columns$.getColumnCssSelector(column)}`,
                    'display: none;'
                );
            });

            // Nested row padding mechanism
            if (!this.enableRowSelection) {
                this.addCSSRule(
                    this.$visibilityStyle,
                    `.${this.gridId} .grid.grouped .grid-child-row ${this.columns$.getColumnCssSelector(firstVisibleColumn)}`,
                    'padding-left: 24px;'
                );
            } else if (this.groupBy) {
                this.addCSSRule(
                    this.$visibilityStyle,
                    `.${this.gridId} .grid.grouped .grid-child-row ${this.columns$.getColumnCssSelector(firstVisibleColumn)}`,
                    'margin-right: -28px;'
                );
            }
        }

        updateColumnsSorting() {
            const columns = this.columns$.columns;
            const orderColumn = this.columns$.getSortingColumn();
            const orderColumnSelector = `.${this.gridId} .grid-header-row ${this.columns$.getColumnCssSelector(orderColumn)}`;
            const orderColumnIconSelector = `${orderColumnSelector} .sorting-icon`;
            const orderColumnRulesList = [
                'font-weight: 800;'
            ];
            const orderColumnIconRulesList = [
                `transform: rotate(${orderColumn.order === 'asc' ? 0 : 180}deg);`,
                'visibility: visible !important;'
            ];
            const sortableColumnRulesList = [
                'cursor: pointer !important;'
            ];

            this.removeCssRules(this.$sortingStyle);

            this.addCSSRule(this.$sortingStyle, orderColumnSelector, _.join(orderColumnRulesList, '\n'));
            this.addCSSRule(this.$sortingStyle, orderColumnIconSelector, _.join(orderColumnIconRulesList, '\n'));

            _.forEach(columns, (column) => {
                if (this.isSortableColumn(column)) {
                    this.addCSSRule(
                        this.$sortingStyle,
                        `.${this.gridId} .grid-header-row ${this.columns$.getColumnCssSelector(column)}`
                        , _.join(sortableColumnRulesList, '\n')
                    );
                }
            });
        }

        onColumnsConfigChange(columnsList) {
            const columnsSwapMap = this.columns$.getSwappedColumnsMap(columnsList);

            this.columns$.hydrate(columnsList, 'configuration');

            if (_.size(columnsSwapMap)) {
                this.dataset$.swapViewDatasetColumns(columnsSwapMap);
            }

            this.updateColumnsVisibility();
            this.updateColumnsSorting();
        }

        /**
         * @ngdoc method
         * @name GridTableController#ensureValue
         * @description Sets default values for optional binding properties
         * @param {string} property The binding name
         * @param {*} defaultValue Default value if the binding value not provided
         * @returns {GridTableController}
         */
        ensureValue(property, defaultValue) {
            if (_.isUndefined(this[property])) {
                this[property] = defaultValue;
            }

            return this;
        }

        /**
         * @ngdoc method
         * @name GridTableController#isSortableColumn
         * @description Is provided column sortable. Takes into account if the grid allows sorting.
         * @param {GridTableColumn} column The column to check sorting for.
         * @returns {boolean}
         */
        isSortableColumn(column) {
            return this.columns$.sortable && column.sortable;
        }

        /**
         * @ngdoc method
         * @name GridTableController#isSortedColumn
         * @description Is provided column currently used in sorting. Takes into account if the grid allows sorting.
         * @param {GridTableColumn} column The column to check sortable for.
         * @returns {boolean}
         */
        isSortedColumn(column) {
            return this.columns$.sortable && column.order;
        }

        /**
         * @ngdoc method
         * @name GridTableController#toggleOrder
         * @description Toggles the sorting for a provided column
         * @param {GridTableColumn} column The column to toggle sorting for.
         * @returns {GridTableController}
         */
        toggleOrder(column) {
            if (this.isSortableColumn(column)) {
                this.columns$.toggleColumnOrder(column);
                this.dataset$.applySorting(column.key, column.order);
                this.updateColumnsSorting();
            }

            return this;
        }

        /**
         * @ngdoc method
         * @name GridTableController#triggerRowClick
         * @description Informs the parent component on row click
         * @param {Event} $event The click event
         * @param {RenderIndexEntry} renderIndexEntry The clicked renderIndexEntry
         * @returns {*}
         */
        triggerRowClick($event, renderIndexEntry) {
            const isGroupRow = renderIndexEntry.isGroupRow;
            const belongsTo = renderIndexEntry.belongsTo;
            const references = renderIndexEntry.references;
            const row = renderIndexEntry.viewDatasetRow.row;

            if (isGroupRow && _.size(references)) {
                this.dataset$.toggleExpandGroupRow(row);
            }

            return this.onRowClick({$event, row, belongsTo, references, isGroupRow});
        }

        /**
         * @ngdoc method
         * @name GridTableController#renderRowsCompleted
         * @description Informs the parent component when a round of rows rendering has completed
         * @returns {*}
         */
        renderRowsCompleted() {
            this.scroller$.checkScrollState();

            return this.onGridRendered();
        }

        /**
         * @ngdoc method
         * @name GridTableController#getRowQaIdentifier
         * @description Computes the view row identifier to be used for the data-qa tag
         * @param {RenderIndexEntry} renderIndexEntry The render index entry of the row
         * @returns {string}
         */
        getRowQaIdentifier(renderIndexEntry) {
            const rowTitle = (renderIndexEntry.isGroupRow)
                ? renderIndexEntry.viewDatasetRow.row.value
                : renderIndexEntry.viewDatasetRow.row[this.rowName];
            const prefix = renderIndexEntry.isGroupRow ? 'group-' : '';

            return `grid-table__${prefix}row-${rowTitle}`;
        }

        /**
         * @ngdoc method
         * @name GridTableController#getSelectRowQaIdentifier
         * @description Computes the view select input identifier to be used for the data-qa tag
         * @param {RenderIndexEntry} renderIndexEntry The render index entry of the row
         * @returns {string}
         */
        getSelectRowQaIdentifier(renderIndexEntry) {
            const rowTitle = (renderIndexEntry.isGroupRow)
                ? renderIndexEntry.viewDatasetRow.row.value
                : renderIndexEntry.viewDatasetRow.row[this.rowName];
            const prefix = renderIndexEntry.isGroupRow ? 'select-group-' : 'select-';

            return `grid-table__${prefix}row-${rowTitle}`;
        }

        /**
         * @ngdoc method
         * @name GridTableController#onRowSelection
         * @description Callback on row selection change
         * @param {Event} $event The click event
         * @param {RenderIndexEntry} renderIndexEntry The clicked row model
         * @returns {GridTableController}
         */
        onRowSelection($event, renderIndexEntry) {
            this.dataset$.onRowSelection(renderIndexEntry);

            return this.notifyRowSelectionChange();
        }

        /**
         * @ngdoc method
         * @name GridTableController#onAllRowsSelection
         * @description Select/deselect ALL rows
         * @returns {GridTableController}
         */
        onAllRowsSelection() {
            this.dataset$.onAllRowsSelection();

            return this.notifyRowSelectionChange();
        }

        /**
         * @ngdoc method
         * @name GridTableController#notifyRowSelectionChange
         * @description Call the onSelectedRowsChange callback to notify the parent component for updates to the selected rows
         * @returns {GridTableController}
         */
        notifyRowSelectionChange() {
            const selectedRows = this.dataset$.getSelectedRowKeys();

            this.onSelectedRowsChange({selectedRows});

            return this;
        }

        /**
         * @ngdoc method
         * @name GridTableController#isCellEditable
         * @description Check if the current row is editable, to enable the edit view for editable cells of the row
         * @param {RenderIndexEntry} renderIndexEntry The render index row
         * @param {number} index The cell index in the columns list
         * @returns {boolean}
         */
        isCellEditable(renderIndexEntry, index) {
            return this.editRowKey === renderIndexEntry.viewDatasetRow.key && renderIndexEntry.columns.columns[index].editable;
        }

        /**
         * @ngdoc method
         * @name GridTableController#triggerEditCellComplete
         * @description Notify the parent component when the cell editing completed
         * @param {string} newCellValue The new cell value
         * @param {string} oldCellValue The old cell value
         * @param {string} rowKey The row key the cell belongs to
         * @param {boolean} isGroupRow Is the row a group row
         */
        triggerEditCellComplete(newCellValue, oldCellValue, rowKey, isGroupRow) {
            this.onEditCellComplete({newCellValue, oldCellValue, rowKey, isGroupRow});
        }

        /**
         * @ngdoc method
         * @name GridTableController#triggerAbortEditCell
         * @description Notify the parent component when the cell editing aborted somehow
         */
        triggerAbortEditCell() {
            if (_.isFunction(this.onAbortCellEdit)) {
                this.onAbortCellEdit();
            }
        }

        /**
         * @ngdoc method
         * @name GridTableController#propagateRearrangeState
         * @description Updates the rearrange state (enabled|disabled) based on the state provided by the parent component
         * @returns {GridTableController}
         */
        propagateRearrangeState() {
            if (this.group && this.enableRearrange) {
                this.rearrange$.enableSortable();
            } else {
                this.rearrange$.disableSortable();
            }

            return this;
        }
    }

    angular.module('wtUi.wtGridTable').component('gridTable', {
        controller: GridTableController,
        templateUrl: 'src/grid-table/grid-table.component.html',
        bindings: {
            /**
             * A flat list of column definitions
             * @type {GridTableColumn[]}
             */
            columns: '<',

            /**
             * A flat array of objects to be rendered as rows of the grid
             * @type {Object[]}
             */
            dataset: '<',

            /**
             * The property to use when identifying the uniqueness of each row in the dataset
             * @type {string}
             */
            rowKey: '<?',

            /**
             * The property to use when identifying the "title" of each row in the dataset. Used to build data-qa tag.
             * Defaults to "title"
             * @type {string}
             */
            rowName: '<?',

            /**
             * Should the table be sortable.
             * When provided, takes priority over the columns sorting definitions.
             * @type {boolean}
             */
            sortable: '<?',

            /**
             * List of filters to apply to the dataset
             * @type {GridTableFilter[]}
             */
            filters: '<?',

            /**
             * Controls the grid size. When it is configured to fill container, the grid will render inside its parent container element, making the grid body scrollable when required.
             * By default the grid will expand to accommodate its size, delegating the scrolling to the page.
             * @type {boolean}
             */
            fillContainer: '<?',

            /**
             * Callback to be executed on every column change.
             * @type {Function}
             */
            onColumnsChange: '&?',

            /**
             * Callback on grid row click.
             * Provides `$event`, the `row`, the `isGroupRow`, `references` and `belongsTo` arguments. For more details see {@link RenderIndexEntry} definition.
             * @type {Function}
             */
            onRowClick: '&?',

            /**
             * Controls the initial behaviour of the expand/collapse when displaying nested data structures
             * @type {boolean}
             */
            // defaultExpandRows: '<?',

            /**
             * List of column keys to expand on initial load
             * @type {string[]}
             */
            // expandedRowKeys: '<?',

            /**
             * Callback to be executed on opening the settings modal.
             * @type {Function}
             */
            onOpenSettingsModal: '&?',

            /**
             * Callback to be executed on cancel/dismis the settings modal.
             * @type {Function}
             */
            onCancelSettingsModal: '&?',

            /**
             * Callback to be executed on grid finished rendering rows.
             * @type {Function}
             */
            onGridRendered: '&?',

            /**
             * Controls the representation of the dataset. By default the rows are represented in a flat list view.
             * @type {boolean}
             * @default false
             */
            group: '<?',

            /**
             * The column key to be used when grouping the grid rows
             * @type {string}
             */
            groupBy: '<?',

            /**
             * List of column configurations used to render the group rows
             * @type {GridTableGroupColumn[]}
             */
            groupColumns: '<?',

            /**
             * Allowing the row selection
             * @type {boolean}
             */
            enableRowSelection: '<?',

            /**
             * Callback to be executed when the selected rows change. Provides the list of selected rows under `selectedRows` parameter.
             * @type {Function}
             */
            onSelectedRowsChange: '&?',

            /**
             * An array of rowKeys to disable the selection for them
             * @type {Array<string>}
             */
            readOnlyRowKeys: '<?',

            /**
             * Row key to trigger edit-view
             * @type {string}
             */
            editRowKey: '<?',

            /**
             * Callback to be executed when editing cell completed
             * Provides the `oldCellValue`, `newCellValue`, `rowKey` and `isGroupRow`
             * @type {Function}
             */
            onEditCellComplete: '&?',

            /**
             * Callback to be executed when aborting editing cell
             * @type {Function}
             */
            onAbortCellEdit: '&?',

            /**
             * Explicit flag to enable/disable rearrange capability
             * @type {boolean}
             */
            enableRearrange: '<?',

            /**
             * Callback to be executed when rows were rearrange by drag and drop
             * Provided parameters are `groupRowKey` as target row and `rowKeys` as rearranged rows
             * @note If the callback is not provided, the drag-and-drop functionality will be dormant.
             * @type {Function}
             */
            onRearrange: '&?',

            /**
             * Extra group collection
             * @type {Array<string>}
             */
            extraGroups: '<?'
        }
    });
})(window.angular);
